import React from 'react'
import styles from './Button.module.css'
// Lightweight className combiner to avoid adding dependency
function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ')
}

/**
 * Generic UI Button component.
 *
 * Props:
 * - variant: visual style (primary | cancel | danger | outline | icon)
 * - size: size variant (sm | md | lg)
 * - loading: show spinner & disable button
 * - fullWidth: stretches to container width
 * - uppercase: transforms label
 * - leftIcon / rightIcon: optional icon elements
 * - as: change underlying element (button | a)
 *
 * Example:
 * <Button variant="primary" onClick={save}>Save</Button>
 * <Button variant="cancel" onClick={onClose}>Cancel</Button>
 * <Button variant="danger" size="sm" leftIcon={<Trash2 size={14}/>}>
 *   Delete
 * </Button>
 */
export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'cancel' | 'danger' | 'outline' | 'icon'
  size?: 'sm' | 'md' | 'lg'
  loading?: boolean
  fullWidth?: boolean
  uppercase?: boolean
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
  as?: 'button' | 'a'
  href?: string
}

const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  loading = false,
  fullWidth = false,
  uppercase = false,
  leftIcon,
  rightIcon,
  className,
  children,
  disabled,
  as = 'button',
  href,
  ...rest
}) => {
  const classes = cx(
    styles.btnBase,
    styles[variant],
    styles[`size-${size}`],
    fullWidth && styles.fullWidth,
    uppercase && styles.uppercase,
    className || ''
  )

  const content = (
    <>
      {loading && <span className={styles.loadingSpinner} aria-hidden />}
      {!loading && leftIcon}
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '.25rem' }}>
        {children}
      </span>
      {!loading && rightIcon}
    </>
  )

  if (as === 'a') {
    return (
      <a
        className={classes}
        aria-disabled={disabled || loading}
        href={href}
        onClick={e => { if (disabled || loading) { e.preventDefault() } }}
      >
        {content}
      </a>
    )
  }

  return (
    <button
      type={rest.type || 'button'}
      className={classes}
      disabled={disabled || loading}
      {...rest}
    >
      {content}
    </button>
  )
}

export default Button
